/**
 * AIInsightsPage.jsx
 * AI-Powered Financial Insights — Nebula Finance
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { ArrowLeft, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Zap } from "lucide-react";
import React from "react";

// ── CONFIG ────────────────────────────────────────────────────────────────────
const SUPABASE_ANON     = process.env.REACT_APP_SUPABASE_ANON_KEY;
const EDGE_FUNCTION_URL = process.env.REACT_APP_EDGE_FUNCTION_URL;

// ── HELPERS ───────────────────────────────────────────────────────────────────
const parseAmt = (v) => parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;
const fmtAmt   = (n, currency = "INR") => {
  const sym = { INR: "₹", USD: "$", EUR: "€", GBP: "£" }[currency] || (currency + " ");
  const num = Number(n) || 0;
  if (currency === "INR") {
    if (num >= 10000000) return `${sym}${(num / 10000000).toFixed(2)}Cr`;
    if (num >= 100000)   return `${sym}${(num / 100000).toFixed(2)}L`;
    if (num >= 1000)     return `${sym}${(num / 1000).toFixed(2)}K`;
    return `${sym}${num.toFixed(0)}`;
  }
  if (num >= 1000000) return `${sym}${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000)    return `${sym}${(num / 1000).toFixed(2)}K`;
  return `${sym}${num.toLocaleString()}`;
};

// ── AI CALL ───────────────────────────────────────────────────────────────────
async function callAI(system, userMessage, maxTokens = 800) {
  const res = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${SUPABASE_ANON}` },
    body: JSON.stringify({
      system,
      max_tokens: maxTokens,
      messages: [{ role: "user", content: userMessage }],
    }),
  });
  if (!res.ok) throw new Error(`AI error ${res.status}`);
  const data  = await res.json();
  const text  = (data.content || []).map(b => b.text || "").join("").trim();
  const clean = text.replace(/```json|```/g, "").trim();
  return JSON.parse(clean);
}

// ── COMPUTE METRICS ───────────────────────────────────────────────────────────
function computeMetrics(profile, hedges, receivables, payables, debtPositions,lcRecords, bcRecords) {
  const activeRec   = (receivables || []).filter(r => r.status !== "Settled");
  const activePay   = (payables    || []).filter(p => p.status !== "Settled");
  const totalRec    = activeRec.reduce((s, r) => s + parseAmt(r.amount), 0);
  const totalPay    = activePay.reduce((s, p) => s + parseAmt(p.amount), 0);
  const totalHedged = (hedges || []).reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);
  const totalExp    = totalRec + totalPay;
  const hedgeRatio  = totalExp > 0 ? (totalHedged / totalExp) * 100 : 0;
  const netExp      = totalRec - totalPay;
  const overdueRec  = activeRec.filter(r => r.status === "Overdue").length;
  const overduePay  = activePay.filter(p => p.status === "Overdue").length;
  const now         = new Date();
  const in30        = new Date(now); in30.setDate(now.getDate() + 30);
  const in10        = new Date(now); in10.setDate(now.getDate() + 10);
const activeLCs      = (lcRecords || []).filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC');
const activeBCs      = (bcRecords || []).filter(r => r.status !== 'Settled');
const lcMaturingSoon = activeLCs.filter(r => { const d = new Date(r.maturity_date); return d >= now && d <= in30; }).length;
const bcRepayingSoon = activeBCs.filter(r => { const d = new Date(r.repayment_date); return d >= now && d <= in30; }).length;
const unhedgedLCs    = activeLCs.filter(r => r.hedge_status === 'Unhedged').length;
const unhedgedBCs    = activeBCs.filter(r => r.hedge_status === 'Unhedged').length;

  const maturingSoon = (hedges || []).filter(h => {
    const d = new Date(h.maturity_date || h.expiry_date);
    return d >= now && d <= in30;
  }).length;
  const recDue10 = activeRec.filter(r => r.due_date && new Date(r.due_date) >= now && new Date(r.due_date) <= in10).length;
  const payDue10 = activePay.filter(p => p.due_date && new Date(p.due_date) >= now && new Date(p.due_date) <= in10).length;

  // Debt pricing vs benchmark — handle both camelCase and snake_case field names
  const validDebt = (debtPositions || []).filter(d =>
    (d.interestRate || d.interest_rate) && (d.market_benchmark || d.marketBenchmark)
  );
  const avgSpread = validDebt.length > 0
    ? validDebt.reduce((s, d) => {
        const rate      = parseFloat(d.interestRate     || d.interest_rate     || 0);
        const benchmark = parseFloat(d.market_benchmark || d.marketBenchmark   || 0);
        return s + (rate - benchmark);
      }, 0) / validDebt.length
    : null;

  return {
    totalRec, totalPay, totalHedged, totalExp,
    hedgeRatio, netExp, overdueRec, overduePay,
    maturingSoon, recDue10, payDue10,
    hedgeCount:    (hedges        || []).length,
    recCount:      activeRec.length,
    payCount:      activePay.length,
    debtCount:     (debtPositions || []).length,
    validDebt:     validDebt.length,
    avgSpread,
    industry:      profile?.industry      || "Manufacturing",
    base_currency: profile?.base_currency || "INR",
    region:        profile?.region        || "India",
    lcMaturingSoon, bcRepayingSoon,
    unhedgedLCs, unhedgedBCs,
    lcCount: activeLCs.length,
    bcCount: activeBCs.length,
  };
}



// ── CALCULATE HEALTH SCORES LOCALLY ──────────────────────────────────────────
function calculateHealthScores(metrics) {
  const hasData = metrics.recCount > 0  || metrics.payCount > 0  ||
                  metrics.hedgeCount > 0 || metrics.debtCount > 0 ||
                  metrics.totalRec > 0   || metrics.totalPay > 0;

  if (!hasData) {
    return {
      overall_score: 0, fx_coverage_score: 0,
      liquidity_score: 0, debt_health_score: 0, collection_score: 0,
      summary: "No treasury data entered yet. Add receivables, payables, hedges and debt to generate your health score.",
      trend: "stable",
    };
  }

  // FX Coverage — based on hedge ratio
  const fxScore = metrics.totalExp === 0 ? 0 : Math.min(100, Math.round(
    metrics.hedgeRatio >= 65 ? 95 :
    metrics.hedgeRatio >= 50 ? 75 + (metrics.hedgeRatio - 50) * 1.3 :
    metrics.hedgeRatio >= 30 ? 50 + (metrics.hedgeRatio - 30) * 1.25 :
    metrics.hedgeRatio * 1.5
  ));

  // Liquidity — based on net exposure
  const liquidityScore = (metrics.recCount === 0 && metrics.payCount === 0) ? 0 :
    Math.min(100, Math.round(
      metrics.netExp >= 0 ? 80 :
      Math.max(20, 80 + (metrics.netExp / Math.max(metrics.totalPay, 1)) * 60)
    ));

  // Collections — based on overdue receivables
  const collectionScore = metrics.recCount === 0 ? 0 :
    Math.min(100, Math.max(0, Math.round(
      100 - (metrics.overdueRec / metrics.recCount) * 100
    )));

  // Debt Health — based on pricing vs market benchmark
  const debtScore = metrics.debtCount === 0 ? 0 :
    metrics.validDebt === 0 ? 50 :
    Math.min(100, Math.round(
      metrics.avgSpread <= 0   ? 95 :
      metrics.avgSpread <= 0.5 ? 80 :
      metrics.avgSpread <= 1.0 ? 65 :
      metrics.avgSpread <= 2.0 ? 45 : 25
    ));

  // Overall — weighted average of active sections only
  const parts = [
    { score: fxScore,         weight: 0.35, active: metrics.totalExp > 0 },
    { score: liquidityScore,  weight: 0.25, active: metrics.recCount > 0 || metrics.payCount > 0 },
    { score: collectionScore, weight: 0.20, active: metrics.recCount > 0 },
    { score: debtScore,       weight: 0.20, active: metrics.debtCount > 0 },
  ];
  const activeParts = parts.filter(p => p.active);
  const totalWeight = activeParts.reduce((s, p) => s + p.weight, 0);
  const overall     = totalWeight === 0 ? 0 : Math.round(
    activeParts.reduce((s, p) => s + p.score * p.weight, 0) / totalWeight
  );

  const trend =
    overall >= 70 && metrics.overdueRec === 0 ? "improving" :
    overall < 45  || metrics.overdueRec > 2   ? "declining" : "stable";

  const summary =
    `Treasury health score is ${overall}/100. ` +
    `FX coverage at ${metrics.hedgeRatio.toFixed(1)}%, ` +
    (metrics.avgSpread !== null
      ? `debt priced ${metrics.avgSpread > 0 ? metrics.avgSpread.toFixed(2) + "% above" : "below"} market benchmark, `
      : "") +
    `${metrics.overdueRec} overdue receivable(s). ` +
    (overall >= 70 ? "Overall position is strong." :
     overall >= 50 ? "Moderate risk — review highlighted areas." :
                     "Immediate attention required on key metrics.");

  return {
    overall_score: overall, fx_coverage_score: fxScore,
    liquidity_score: liquidityScore, debt_health_score: debtScore,
    collection_score: collectionScore, summary, trend,
  };
}

// ── HEALTH SCORE GAUGE ────────────────────────────────────────────────────────
function HealthGauge({ score, label, isMain = false }) {
  const canvasRef = React.useRef(null);
  const scoreColor = (s) => s < 40 ? "#e24b4a" : s < 60 ? "#ef9f27" : s < 75 ? "#63992a" : "#1d9e75";

  const drawGauge = (canvas, currentScore) => {
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const cx = canvas.width / 2;
    const cy = isMain ? canvas.height * 0.82 : canvas.height * 0.88;
    const r  = isMain ? canvas.height * 0.72 : canvas.height * 0.78;
    const startAngle = Math.PI;
    const scoreAngle = startAngle + (currentScore / 100) * Math.PI;

    for (let i = 0; i <= 10; i++) {
      const a = startAngle + (i / 10) * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx + (r + (isMain ? 6 : 4)) * Math.cos(a), cy + (r + (isMain ? 6 : 4)) * Math.sin(a));
      ctx.lineTo(cx + (r + (isMain ? 2 : 1)) * Math.cos(a), cy + (r + (isMain ? 2 : 1)) * Math.sin(a));
      ctx.strokeStyle = "rgba(128,128,128,0.3)"; ctx.lineWidth = 1.5; ctx.stroke();
    }

    [{ from: 0, to: 40, color: "#f09595" }, { from: 40, to: 60, color: "#fac775" },
     { from: 60, to: 75, color: "#c0dd97" }, { from: 75, to: 100, color: "#5dcaa5" }].forEach(seg => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, startAngle + (seg.from / 100) * Math.PI, startAngle + (seg.to / 100) * Math.PI);
      ctx.strokeStyle = seg.color; ctx.lineWidth = isMain ? 14 : 9; ctx.lineCap = "butt"; ctx.stroke();
    });

    ctx.beginPath();
    ctx.arc(cx, cy, r, startAngle, scoreAngle);
    ctx.strokeStyle = scoreColor(currentScore); ctx.lineWidth = isMain ? 14 : 9; ctx.lineCap = "round"; ctx.stroke();

    const needleLen = r * 0.88;
    ctx.save(); ctx.translate(cx, cy); ctx.rotate(scoreAngle);
    ctx.beginPath();
    ctx.moveTo(-(isMain ? 4 : 2.5), 0); ctx.lineTo(needleLen, 0);
    ctx.lineTo(-(isMain ? 1.2 : 0.75), -(isMain ? 2.4 : 1.5));
    ctx.lineTo(-(isMain ? 1.2 : 0.75),  (isMain ? 2.4 : 1.5));
    ctx.closePath(); ctx.fillStyle = scoreColor(currentScore); ctx.fill(); ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, isMain ? 5 : 3.5, 0, 2 * Math.PI);
    ctx.fillStyle = scoreColor(currentScore); ctx.fill();
  };

  React.useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const start  = performance.now();
    const animate = (now) => {
      const t    = Math.min((now - start) / 1000, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      drawGauge(canvas, score * ease);
      if (t < 1) requestAnimationFrame(animate); else drawGauge(canvas, score);
    };
    requestAnimationFrame(animate);
  }, [score]);

  const zone  = score < 40 ? "Critical" : score < 60 ? "Needs attention" : score < 75 ? "Good" : "Excellent";
  const color = scoreColor(score);

  return (
    <div style={{ textAlign: "center" }}>
      <canvas ref={canvasRef} width={isMain ? 220 : 120} height={isMain ? 130 : 72} />
      <div style={{ fontSize: isMain ? 28 : 18, fontWeight: 800, color, marginTop: -6 }}>{score}</div>
      {isMain && <div style={{ fontSize: 12, color, marginTop: 3 }}>{zone}</div>}
      <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.07em", marginTop: 4 }}>{label}</div>
    </div>
  );
}

// ── SECTION CARD ──────────────────────────────────────────────────────────────
function SectionCard({ title, icon, accentColor = "#6366f1", loading, children, onRefresh }) {
  return (
    <div style={{ background: "#12141a", border: "1px solid #2a2d3a", borderRadius: 12, overflow: "hidden", borderLeft: `3px solid ${accentColor}` }}>
      <div style={{ padding: "14px 20px", borderBottom: "1px solid #2a2d3a", display: "flex", alignItems: "center", gap: 10, background: "#0e1018" }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", flex: 1 }}>{title}</span>
        {onRefresh && (
          <button onClick={onRefresh} disabled={loading} style={{ background: "none", border: "none", cursor: loading ? "not-allowed" : "pointer", color: "#64748b", display: "flex", alignItems: "center" }}>
            <RefreshCw size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          </button>
        )}
        {loading && !onRefresh && <div style={{ width: 14, height: 14, border: "2px solid #6366f1", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />}
      </div>
      <div style={{ padding: "18px 20px" }}>
        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[1,2,3].map(i => <div key={i} style={{ height: 16, background: "#1a1d26", borderRadius: 4, animation: "pulse 1.5s ease-in-out infinite", width: i === 3 ? "60%" : "100%" }} />)}
            <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
          </div>
        ) : children}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function AIInsightsPage({ onBack, profile, hedges = [], receivables = [], payables = [], companyId, debtPositions = [], liveRates = {}, lcRecords = [], bcRecords = [] }) {
  const [healthData,      setHealthData]      = useState(null);
  const [marketData,      setMarketData]      = useState(null);
  const [alertsData,      setAlertsData]      = useState(null);
  const [benchmarkData,   setBenchmarkData]   = useState(null);
  const [actionablesData, setActionablesData] = useState(null);

  const [loadingHealth,      setLoadingHealth]      = useState(true);
  const [loadingMarket,      setLoadingMarket]      = useState(true);
  const [loadingAlerts,      setLoadingAlerts]      = useState(true);
  const [loadingBenchmark,   setLoadingBenchmark]   = useState(true);
  const [loadingActionables, setLoadingActionables] = useState(true);

  const metrics = computeMetrics(profile, hedges, receivables, payables, debtPositions, lcRecords, bcRecords);

  // ── HEALTH SCORE (local calculation — no AI) ──────────────────────────────
  const fetchHealth = useCallback(() => {
    setLoadingHealth(true);
    const scores = calculateHealthScores(metrics);
    setHealthData(scores);
    setLoadingHealth(false);
  }, [
    metrics.recCount, metrics.payCount, metrics.hedgeCount, metrics.debtCount,
    metrics.hedgeRatio, metrics.netExp, metrics.overdueRec, metrics.avgSpread,
    metrics.validDebt, metrics.totalExp,
  ]);

  // ── MARKET BRIEF ─────────────────────────────────────────────────────────
const fetchMarket = useCallback(async () => {
  setLoadingMarket(true);
  try {
    const rateContext = liveRates?.usdinr
      ? `Current live rates from market feed: USD/INR=${liveRates.usdinr}, EUR/INR=${liveRates.eurinr}, GBP/INR=${liveRates.gbpinr}. Use these exact values in the rates array.`
      : "Use current accurate market rates as of today.";

    const data = await callAI(
      `You are a senior FX analyst covering Indian markets. Return ONLY valid JSON:
{
  "usdinr_level": "current USD/INR level and direction in one sentence",
  "rbi_stance": "RBI policy stance in one sentence",
  "key_event": "one key global macro event affecting INR today",
  "implication": "one actionable implication for Indian corporates with FX exposure",
  "rates": [
    {"pair":"USD/INR","value":"xx.xx","trend":"up or down or stable"},
    {"pair":"EUR/INR","value":"xx.xx","trend":"up or down or stable"},
    {"pair":"GBP/INR","value":"xx.xx","trend":"up or down or stable"}
  ]
}`,
      `Today is ${new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}. ${rateContext} Company: ${metrics.industry} sector, hedge ratio ${metrics.hedgeRatio.toFixed(1)}%.`,
      500
    );

    // ── Override AI rates with live rates ──────────────────────────────
    if (liveRates?.usdinr && data.rates) {
      data.rates = data.rates.map(r => {
        if (r.pair === "USD/INR") return { ...r, value: liveRates.usdinr?.toString() };
        if (r.pair === "EUR/INR") return { ...r, value: liveRates.eurinr?.toString() };
        if (r.pair === "GBP/INR") return { ...r, value: liveRates.gbpinr?.toString() };
        return r;
      });
    } 
    setMarketData(data);

  } catch {

    setMarketData({ usdinr_level: liveRates?.usdinr ? `USD/INR is trading at ${liveRates.usdinr}.` : "Live market data unavailable. Please refresh.", rbi_stance: "—", key_event: "—", implication: "Refresh to load AI-powered market context.", 
      rates: liveRates?.usdinr ? [
        { pair: "USD/INR", value: liveRates.usdinr?.toString(), trend: "stable" },
        { pair: "EUR/INR", value: liveRates.eurinr?.toString(), trend: "stable" },
        { pair: "GBP/INR", value: liveRates.gbpinr?.toString(), trend: "stable" },
      ] : [],
    });


  } finally { setLoadingMarket(false); }
}, [metrics.hedgeRatio, metrics.industry, liveRates]);

  // ── PREDICTIVE ALERTS ─────────────────────────────────────────────────────
  const fetchAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const data = await callAI(
        `You are a treasury risk analyst. Return ONLY valid JSON:
{
  "alerts": [
    {
      "type": "maturity" or "cashflow" or "fx_risk" or "overdue" or "hedge_gap",
      "severity": "critical" or "warning" or "info",
      "title": "max 8 words",
      "detail": "1-2 sentences with specific numbers",
      "action": "specific action in max 12 words",
      "days": number or null
    }
  ]
}
Generate 3-5 forward-looking alerts based on the data.`,
        `Metrics: maturing_30d=${metrics.maturingSoon}, rec_due_10d=${metrics.recDue10}, pay_due_10d=${metrics.payDue10}, overdue_rec=${metrics.overdueRec}, overdue_pay=${metrics.overduePay}, hedge_ratio=${metrics.hedgeRatio.toFixed(1)}%, net_exposure=${fmtAmt(Math.abs(metrics.netExp))}, total_hedged=${fmtAmt(metrics.totalHedged)}`,
        600
      );

      
     
setAlertsData(data);
    } catch {
      const alerts = [];
      if (metrics.maturingSoon > 0)   alerts.push({ type: "maturity",  severity: "warning",  title: `${metrics.maturingSoon} hedge contracts maturing soon`,                          detail: `${metrics.maturingSoon} hedge contract(s) maturing within 30 days. Review rollover or settlement options.`,                                                                  action: "Contact bank to discuss rollover options",        days: 30   });
      if (metrics.overdueRec > 0)     alerts.push({ type: "overdue",   severity: "critical", title: `${metrics.overdueRec} overdue receivables`,                                      detail: `${metrics.overdueRec} receivable(s) are overdue, increasing FX exposure unnecessarily.`,                                                                                     action: "Escalate collections immediately",                days: null });
      if (metrics.hedgeRatio < 40)    alerts.push({ type: "hedge_gap", severity: "critical", title: "Hedge ratio below recommended level",                                             detail: `Hedge ratio ${metrics.hedgeRatio.toFixed(1)}% is below 50-60% benchmark.`,                                                                                                  action: "Enter forward contracts to increase coverage",    days: null });
      if (metrics.recDue10 > 0)       alerts.push({ type: "cashflow",  severity: "info",     title: `${metrics.recDue10} receivables due in 10 days`,                                 detail: `${metrics.recDue10} receivable(s) due within 10 days. Follow up for timely collection.`,                                                                                     action: "Send payment reminders to counterparties",        days: 10   });
      if (metrics.payDue10 > 0)       alerts.push({ type: "cashflow",  severity: "warning",  title: `${metrics.payDue10} payables due in 10 days`,                                    detail: `${metrics.payDue10} payable(s) due within 10 days. Ensure sufficient liquidity.`,                                                                                            action: "Confirm funds availability",                      days: 10   });
      if (metrics.lcMaturingSoon > 0) alerts.push({ type: "maturity",  severity: "warning",  title: `${metrics.lcMaturingSoon} LC${metrics.lcMaturingSoon > 1 ? 's' : ''} maturing within 30 days`,  detail: `${metrics.lcMaturingSoon} Letter${metrics.lcMaturingSoon > 1 ? 's' : ''} of Credit maturing soon. Consider settlement or conversion to Buyers Credit.`,             action: "Review LC maturity actions — Settle or Avail BC", days: 30   });
      if (metrics.bcRepayingSoon > 0) alerts.push({ type: "cashflow",  severity: "warning",  title: `${metrics.bcRepayingSoon} BC repayment${metrics.bcRepayingSoon > 1 ? 's' : ''} due within 30 days`, detail: `${metrics.bcRepayingSoon} Buyers Credit repayment${metrics.bcRepayingSoon > 1 ? 's' : ''} due within 30 days. Ensure FX and funds are arranged.`,           action: "Arrange FCY funds for BC repayment",              days: 30   });
      if (metrics.unhedgedLCs > 0)    alerts.push({ type: "hedge_gap", severity: metrics.unhedgedLCs > 2 ? "critical" : "warning", title: `${metrics.unhedgedLCs} unhedged LC${metrics.unhedgedLCs > 1 ? 's' : ''}`, detail: `${metrics.unhedgedLCs} active LC${metrics.unhedgedLCs > 1 ? 's' : ''} without hedge cover. Open FX exposure on import payables.`, action: "Add forward cover for unhedged LCs", days: null });
      setAlertsData({ alerts: alerts.length > 0 ? alerts : [{ type: "info", severity: "info", title: "No immediate alerts", detail: "Treasury position looks stable with no critical near-term issues.", action: "Continue monitoring daily", days: null }] });
    } finally { setLoadingAlerts(false); }
  }, [metrics.maturingSoon, metrics.overdueRec, metrics.hedgeRatio, metrics.recDue10, metrics.payDue10, metrics.netExp, metrics.totalHedged, metrics.overduePay]);

  // ── PEER BENCHMARKING ─────────────────────────────────────────────────────
  const fetchBenchmark = useCallback(async () => {
    setLoadingBenchmark(true);
    try {
      const data = await callAI(
        `You are a treasury benchmarking analyst. Return ONLY valid JSON:
{
  "benchmarks": [
    {
      "metric": "metric name",
      "company_value": "your value as string",
      "industry_avg": "industry avg as string",
      "best_practice": "best practice as string",
      "status": "above" or "below" or "at",
      "comment": "one sentence insight"
    }
  ]
}
Generate 4-5 benchmarks for key treasury metrics.`,
        `Company data: industry=${metrics.industry}, hedge_ratio=${metrics.hedgeRatio.toFixed(1)}%, overdue_receivables=${metrics.overdueRec}, maturing_contracts=${metrics.maturingSoon}, net_exposure_inr=${fmtAmt(Math.abs(metrics.netExp))}, avg_debt_spread=${metrics.avgSpread !== null ? metrics.avgSpread.toFixed(2) + "%" : "N/A"}`,
        600
      );
      setBenchmarkData(data);
    } catch {
      setBenchmarkData({
        benchmarks: [
          { metric: "Hedge Ratio",         company_value: `${metrics.hedgeRatio.toFixed(1)}%`, industry_avg: "50-60%",       best_practice: "60-70%",              status: metrics.hedgeRatio >= 50 ? "above" : "below",                            comment: metrics.hedgeRatio >= 50 ? "Coverage within industry norms." : "Increase hedge ratio to benchmark." },
          { metric: "Overdue Receivables", company_value: `${metrics.overdueRec}`,             industry_avg: "< 2%",         best_practice: "0%",                  status: metrics.overdueRec === 0 ? "above" : "below",                            comment: metrics.overdueRec === 0 ? "Excellent collections track record." : "Overdue items increase FX risk." },
          { metric: "Debt vs Benchmark",   company_value: metrics.avgSpread !== null ? `+${metrics.avgSpread.toFixed(2)}%` : "N/A", industry_avg: "0-0.5% over", best_practice: "At or below benchmark", status: metrics.avgSpread !== null && metrics.avgSpread <= 0.5 ? "above" : "below", comment: "Debt above benchmark indicates renegotiation opportunity." },
          { metric: "FX Coverage",         company_value: `${metrics.hedgeRatio.toFixed(0)}%`, industry_avg: "55%",          best_practice: "65%",                 status: metrics.hedgeRatio >= 55 ? "at" : "below",                               comment: "Target 65% for optimal risk-adjusted protection." },
        ],
      });
    } finally { setLoadingBenchmark(false); }
  }, [metrics.industry, metrics.hedgeRatio, metrics.overdueRec, metrics.maturingSoon, metrics.netExp, metrics.avgSpread]);

  // ── TREASURY ACTIONABLES ──────────────────────────────────────────────────
  const fetchActionables = useCallback(async () => {
    setLoadingActionables(true);
    try {
      const data = await callAI(
        `You are a senior treasury advisor. Return ONLY valid JSON:
{
  "actionables": [
    {
      "rank": 1,
      "priority": "immediate" or "this_week" or "this_month",
      "category": "hedging" or "liquidity" or "collections" or "payments" or "strategy",
      "action": "clear action in max 10 words",
      "rationale": "why this matters in 1-2 sentences",
      "impact": "high" or "medium" or "low",
      "deadline": "timeframe"
    }
  ]
}
Generate 5 prioritised treasury actions.`,
        `Metrics: hedge_ratio=${metrics.hedgeRatio.toFixed(1)}%, overdue_rec=${metrics.overdueRec}, maturing_soon=${metrics.maturingSoon}, rec_due_10d=${metrics.recDue10}, pay_due_10d=${metrics.payDue10}, industry=${metrics.industry}, avg_debt_spread=${metrics.avgSpread !== null ? metrics.avgSpread.toFixed(2) + "%" : "N/A"}`,
        700
      );
      setActionablesData(data);
    } catch {
      const actionables = [];
      if (metrics.hedgeRatio < 50)  actionables.push({ rank: 1, priority: "immediate",  category: "hedging",     action: "Increase hedge ratio to minimum 50%",             rationale: `Hedge ratio of ${metrics.hedgeRatio.toFixed(1)}% is below benchmark. Significant P&L risk.`,                                                                              impact: "high",   deadline: "This week"    });
      if (metrics.maturingSoon > 0) actionables.push({ rank: 2, priority: "this_week",  category: "hedging",     action: "Review maturing contracts for rollover",           rationale: `${metrics.maturingSoon} contract(s) mature within 30 days. Early action avoids last-minute rate pressure.`,                                                             impact: "high",   deadline: "Within 7 days" });
      if (metrics.overdueRec > 0)   actionables.push({ rank: 3, priority: "immediate",  category: "collections", action: "Escalate overdue receivables collections",         rationale: `${metrics.overdueRec} overdue receivable(s) extend open FX exposure unnecessarily.`,                                                                                    impact: "high",   deadline: "Today"        });
      if (metrics.payDue10 > 0)     actionables.push({ rank: 4, priority: "this_week",  category: "payments",    action: "Confirm liquidity for upcoming payments",          rationale: `${metrics.payDue10} payable(s) due in 10 days. Ensure funds availability to avoid penalties.`,                                                                           impact: "medium", deadline: "Within 5 days" });
      if (metrics.avgSpread !== null && metrics.avgSpread > 1) actionables.push({ rank: 5, priority: "this_month", category: "strategy", action: "Renegotiate debt above market benchmark", rationale: `Debt priced ${metrics.avgSpread.toFixed(2)}% above benchmark. Renegotiation could reduce interest costs.`, impact: "high", deadline: "This month" });
      actionables.push({ rank: actionables.length + 1, priority: "this_month", category: "strategy", action: "Review FX policy and hedge targets quarterly", rationale: "Regular review ensures hedge strategy stays aligned with business exposure and market conditions.", impact: "medium", deadline: "This month" });
      setActionablesData({ actionables });
    } finally { setLoadingActionables(false); }
  }, [metrics.hedgeRatio, metrics.overdueRec, metrics.maturingSoon, metrics.recDue10, metrics.payDue10, metrics.industry, metrics.avgSpread]);

  useEffect(() => {
    if (!profile) return;
    fetchHealth();
    fetchMarket();
    fetchAlerts();
    fetchBenchmark();
    fetchActionables();
  }, [profile]);

  const SEVERITY_COLORS = {
    critical: { color: "#f87171", bg: "rgba(248,113,113,0.1)", border: "rgba(248,113,113,0.2)" },
    warning:  { color: "#fbbf24", bg: "rgba(251,191,36,0.1)",  border: "rgba(251,191,36,0.2)"  },
    info:     { color: "#818cf8", bg: "rgba(99,102,241,0.1)",  border: "rgba(99,102,241,0.2)"  },
  };
  const PRIORITY_COLORS = {
    immediate:  { color: "#f87171", label: "Immediate"  },
    this_week:  { color: "#fbbf24", label: "This Week"  },
    this_month: { color: "#818cf8", label: "This Month" },
  };
  const CAT_ICONS = {
    hedging: "🛡️", liquidity: "💧", collections: "📥", payments: "📤", strategy: "🎯",
    maturity: "📅", cashflow: "💰", fx_risk: "📈", overdue: "⚠️", hedge_gap: "🛡️",
  };

  if (!profile) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
      <div style={{ width: 32, height: 32, border: "3px solid #6366f1", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ background: "#0a0b0f", minHeight: "100vh", fontFamily: "sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ padding: "20px 28px", borderBottom: "1px solid #2a2d3a", background: "#12141a", display: "flex", alignItems: "center", gap: 14 }}>
        <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, border: "1px solid #2a2d3a", background: "transparent", color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <ArrowLeft size={14} /> Back
        </button>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>✦ AI-Powered Financial Insights</h1>
          <p style={{ fontSize: 11, color: "#64748b", margin: "3px 0 0" }}>{profile?.company_name} · {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</p>
        </div>
        <button onClick={() => { fetchHealth(); fetchMarket(); fetchAlerts(); fetchBenchmark(); fetchActionables(); }}
          style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", borderRadius: 8, border: "1px solid #2a2d3a", background: "transparent", color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          <RefreshCw size={13} /> Refresh All
        </button>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 28px", display: "flex", flexDirection: "column", gap: 24 }}>

        {/* Health Score */}
        <SectionCard title="Treasury Health Score" icon="🏥" accentColor="#4ade80" loading={loadingHealth} onRefresh={fetchHealth}>
          {healthData && (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 20, marginBottom: 20 }}>
                <HealthGauge score={healthData.overall_score}     label="Overall"     isMain={true} />
                <HealthGauge score={healthData.fx_coverage_score} label="FX Coverage" />
                <HealthGauge score={healthData.liquidity_score}   label="Liquidity"   />
                <HealthGauge score={healthData.debt_health_score} label="Debt Health" />
                <HealthGauge score={healthData.collection_score}  label="Collections" />
              </div>
              <div style={{ background: "#0e1018", borderRadius: 8, padding: "12px 16px", borderLeft: `3px solid ${healthData.overall_score >= 70 ? "#4ade80" : healthData.overall_score >= 40 ? "#fbbf24" : "#f87171"}`, display: "flex", alignItems: "center", gap: 10 }}>
                {healthData.trend === "improving" ? <TrendingUp size={16} color="#4ade80" /> : healthData.trend === "declining" ? <TrendingDown size={16} color="#f87171" /> : <CheckCircle size={16} color="#fbbf24" />}
                <p style={{ fontSize: 13, color: "#94a3b8", margin: 0 }}>{healthData.summary}</p>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Market Context + Predictive Alerts */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <SectionCard title="Market Context" icon="📊" accentColor="#818cf8" loading={loadingMarket} onRefresh={fetchMarket}>
            {marketData && (
              <div>
                <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                  {(marketData.rates || []).map((r, i) => (
                    <div key={i} style={{ background: "#0e1018", borderRadius: 8, padding: "8px 14px", border: "1px solid #2a2d3a", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#64748b", marginBottom: 2 }}>{r.pair}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#f1f5f9" }}>{r.value}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, marginTop: 2, color: r.trend === "up" ? "#f87171" : r.trend === "down" ? "#4ade80" : "#fbbf24" }}>
                        {r.trend === "up" ? "▲" : r.trend === "down" ? "▼" : "→"} {r.trend}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { label: "USD/INR",    text: marketData.usdinr_level },
                    { label: "RBI Stance", text: marketData.rbi_stance   },
                    { label: "Key Event",  text: marketData.key_event    },
                    { label: "Action",     text: marketData.implication, bold: true },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: 10 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: "#818cf8", minWidth: 72, paddingTop: 1 }}>{item.label}</span>
                      <span style={{ fontSize: 12, color: item.bold ? "#f1f5f9" : "#94a3b8", lineHeight: 1.6, fontWeight: item.bold ? 600 : 400 }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Predictive Alerts" icon="🔔" accentColor="#f87171" loading={loadingAlerts} onRefresh={fetchAlerts}>
            {alertsData && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {(alertsData.alerts || []).map((alert, i) => {
                  const sev = SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.info;
                  return (
                    <div key={i} style={{ background: sev.bg, border: `1px solid ${sev.border}`, borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>{CAT_ICONS[alert.type] || "⚡"}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: sev.color }}>{alert.title}</span>
                        {alert.days && <span style={{ marginLeft: "auto", fontSize: 9, color: "#64748b" }}><Clock size={10} style={{ display: "inline", marginRight: 3 }} />{alert.days}d</span>}
                      </div>
                      <p style={{ fontSize: 11, color: "#94a3b8", margin: "0 0 6px", lineHeight: 1.55 }}>{alert.detail}</p>
                      <span style={{ fontSize: 10, fontWeight: 600, color: sev.color }}>→ {alert.action}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </SectionCard>
        </div>

        {/* Peer Benchmarking */}
        <SectionCard title="Peer Benchmarking" icon="📐" accentColor="#fbbf24" loading={loadingBenchmark} onRefresh={fetchBenchmark}>
          {benchmarkData && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#0e1018" }}>
                    {["Metric", "Your Value", "Industry Avg", "Best Practice", "Status", "Insight"].map(h => (
                      <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: "1px solid #2a2d3a" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(benchmarkData.benchmarks || []).map((b, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #1e2130", background: i % 2 === 0 ? "transparent" : "#0e1018" }}>
                      <td style={{ padding: "10px 14px", fontWeight: 700, color: "#f1f5f9" }}>{b.metric}</td>
                      <td style={{ padding: "10px 14px", fontWeight: 800, color: b.status === "above" ? "#4ade80" : b.status === "below" ? "#f87171" : "#fbbf24" }}>{b.company_value}</td>
                      <td style={{ padding: "10px 14px", color: "#64748b" }}>{b.industry_avg}</td>
                      <td style={{ padding: "10px 14px", color: "#64748b" }}>{b.best_practice}</td>
                      <td style={{ padding: "10px 14px" }}>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: b.status === "above" ? "rgba(74,222,128,0.1)" : b.status === "below" ? "rgba(248,113,113,0.1)" : "rgba(251,191,36,0.1)", color: b.status === "above" ? "#4ade80" : b.status === "below" ? "#f87171" : "#fbbf24", border: `1px solid ${b.status === "above" ? "rgba(74,222,128,0.2)" : b.status === "below" ? "rgba(248,113,113,0.2)" : "rgba(251,191,36,0.2)"}` }}>
                          {b.status === "above" ? "✓ Above" : b.status === "below" ? "↓ Below" : "→ At Avg"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 11 }}>{b.comment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Treasury Actionables */}
        <SectionCard title="Treasury Actionables" icon="✅" accentColor="#2dd4bf" loading={loadingActionables} onRefresh={fetchActionables}>
          {actionablesData && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(actionablesData.actionables || []).map((a, i) => {
                const prio        = PRIORITY_COLORS[a.priority] || PRIORITY_COLORS.this_month;
                const impactColor = a.impact === "high" ? "#f87171" : a.impact === "medium" ? "#fbbf24" : "#4ade80";
                return (
                  <div key={i} style={{ background: "#0e1018", borderRadius: 8, padding: "14px 16px", border: "1px solid #2a2d3a", display: "flex", gap: 14, alignItems: "flex-start" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#818cf8" }}>{a.rank}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14 }}>{CAT_ICONS[a.category] || "✅"}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9" }}>{a.action}</span>
                        <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: `${prio.color}18`, color: prio.color, border: `1px solid ${prio.color}33` }}>{prio.label}</span>
                        <span style={{ marginLeft: "auto", fontSize: 10, color: "#64748b" }}>{a.deadline}</span>
                      </div>
                      <p style={{ fontSize: 12, color: "#94a3b8", margin: "0 0 8px", lineHeight: 1.6 }}>{a.rationale}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Zap size={11} color={impactColor} />
                        <span style={{ fontSize: 10, color: impactColor, fontWeight: 600, textTransform: "uppercase" }}>{a.impact} impact</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>

      </div>
    </div>
  );
}
